import { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Loader2, ShieldCheck, ShieldAlert, History } from "lucide-react";
import { api } from "../../lib/api";
import { formatFullCurrency } from "./projectUtils";

export function AuditPanelTab({ token, balances }) {
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getRdDeviationAlerts(token).then(setAlerts).catch(() => setAlerts([])).finally(() => setLoading(false));
  }, [token]);

  const activeProjects = balances.filter((b) => b.manual_deviation_flag);
  const affectedProjectNames = new Set((alerts || []).map((a) => a.project_name));

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="audit-panel-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card><CardContent className="pt-4">
          <p className="text-xs text-muted-foreground">Desviaciones activas</p>
          <p className="text-2xl font-bold text-red-600" data-testid="audit-active-count">{activeProjects.length}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-muted-foreground">Total avisos históricos</p>
          <p className="text-2xl font-bold" data-testid="audit-total-count">{alerts?.length || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <p className="text-xs text-muted-foreground">Proyectos afectados alguna vez</p>
          <p className="text-2xl font-bold" data-testid="audit-affected-count">{affectedProjectNames.size}</p>
        </CardContent></Card>
      </div>

      <Card className={activeProjects.length > 0 ? "border-red-200 dark:border-red-900" : ""}>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 mb-3">
            {activeProjects.length > 0
              ? <ShieldAlert className="w-4 h-4 text-red-600" />
              : <ShieldCheck className="w-4 h-4 text-green-600" />}
            <h3 className="font-semibold text-sm">Desviaciones activas ahora ({activeProjects.length})</h3>
          </div>
          {activeProjects.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ningún proyecto se aleja actualmente de la fuente verificada. Todos los 27 balances de I+D coinciden con el Excel reconciliado el 2026-08-21.</p>
          ) : (
            <div className="space-y-2">
              {activeProjects.map((p) => (
                <div key={p.id} className="flex items-center justify-between p-3 rounded-lg border bg-red-50 dark:bg-red-950/20" data-testid={`audit-active-${p.id}`}>
                  <div>
                    <p className="font-medium text-sm">{p.project_name}</p>
                    <p className="text-xs text-muted-foreground">Campos desviados: {p.manual_deviation_fields?.join(", ")}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">{p.manual_deviation_at ? new Date(p.manual_deviation_at).toLocaleString("es-ES") : ""}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-muted-foreground" />
            <h3 className="font-semibold text-sm">Historial completo de avisos</h3>
          </div>
          {!alerts?.length ? (
            <p className="text-sm text-muted-foreground">No se ha registrado ninguna edición manual que se aleje de la fuente verificada.</p>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {alerts.map((a) => (
                <div key={a.id} className="p-3 rounded-lg border text-sm" data-testid={`audit-history-${a.id}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="secondary">{a.project_name}</Badge>
                    <span className="text-xs text-muted-foreground">
                      editado por {a.edited_by} · {new Date(a.edited_at).toLocaleString("es-ES")}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {a.deviations.map((d) => (
                      <p key={d.field} className="text-xs text-muted-foreground">
                        <span className="font-mono">{d.field}</span>: verificado {formatFullCurrency(d.verified_value)} → guardado {formatFullCurrency(d.new_value)}{" "}
                        <span className={d.difference_eur < 0 ? "text-red-600" : "text-green-600"}>
                          ({d.difference_eur > 0 ? "+" : ""}{formatFullCurrency(d.difference_eur)})
                        </span>
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
